import { useState, useEffect, useRef } from 'react';

const faqs = [
  {
    question: 'What is life coaching?',
    answer:
      'Life coaching is a collaborative process where a trained coach helps you gain clarity on your goals, identify what is holding you back, and build a practical plan to move forward. Unlike therapy, coaching focuses on the present and future — not the past. Think of it as having a thinking partner in your corner who helps you get out of your own way and start making real progress.',
  },
  {
    question: 'Is life coaching right for me?',
    answer:
      'If you feel stuck, overwhelmed, or like you\'re not living up to your potential, coaching can be a powerful catalyst for change. It\'s a great fit if you\'re motivated to grow but unsure where to start, if you have goals but keep getting in your own way, or if you simply want more clarity and direction in your life. Our free consultation is the perfect way to find out if it\'s the right fit for you — no pressure, no commitment.',
  },
  {
    question: 'How many sessions will I need?',
    answer:
      'That depends on your goals and where you\'re starting from. Some clients experience meaningful breakthroughs in just a few sessions. Others prefer ongoing support over several months as they work toward bigger life changes. We tailor the journey to you — there is no one-size-fits-all timeline. We will talk through this together during your free consultation.',
  },
  {
    question: 'What happens during a free consultation?',
    answer:
      'The free consultation is a relaxed, honest conversation — not a sales pitch. We\'ll talk about where you are right now, what you\'re hoping to change or achieve, and what challenges have been getting in your way. You\'ll leave with clarity and some useful insight, regardless of whether you decide to continue. It is completely no-obligation.',
  },
  {
    question: 'How is coaching different from therapy?',
    answer:
      'Therapy often focuses on healing past wounds, processing trauma, and working through mental health challenges with a licensed clinician. Coaching, by contrast, is forward-focused. We start from where you are today and work toward where you want to be. Coaching is not a substitute for therapy, and we\'ll always encourage you to seek mental health support if that\'s what you need.',
  },
  {
    question: 'Do I have to be in Lubbock to work with you?',
    answer:
      'Not at all! While we are proudly rooted in Lubbock, Texas, we work with clients virtually as well. Whether you\'re across town or across the state, you can access the same quality of support. Many of our clients prefer the flexibility of virtual sessions from the comfort of their own home.',
  },
  {
    question: 'What does coaching actually cost?',
    answer:
      'We offer a free first consultation so you can experience the value before making any commitment. Pricing for ongoing coaching is discussed personally — because every client\'s needs are different, we do not believe in a one-size-fits-all package. Reach out and we\'ll find an approach that works for you and your budget.',
  },
  {
    question: 'How do I get started?',
    answer:
      'It\'s simple — just fill out the short form on this page or click any "Book Your Free Consultation" button. We\'ll reach out to schedule a time that works for you. Your first step toward a Brand New You is just one click away.',
  },
];

function FaqItem({
  faq,
  index,
  isOpen,
  onToggle,
  visible,
}: {
  faq: { question: string; answer: string };
  index: number;
  isOpen: boolean;
  onToggle: () => void;
  visible: boolean;
}) {
  const contentRef = useRef<HTMLDivElement>(null);

  return (
    <div
      className={`border-b border-[#E8DDD4] last:border-b-0 transition-all duration-500 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
      }`}
      style={{ transitionDelay: `${index * 60}ms` }}
    >
      <button
        onClick={onToggle}
        className="w-full flex items-start gap-5 py-6 text-left cursor-pointer group"
        aria-expanded={isOpen}
      >
        {/* Number */}
        <span
          className="text-sm font-semibold text-[#D4896A] shrink-0 mt-0.5 w-6"
          style={{ fontFamily: 'Inter, sans-serif' }}
        >
          {String(index + 1).padStart(2, '0')}
        </span>

        {/* Question */}
        <span
          className={`flex-1 text-base md:text-lg font-semibold transition-colors duration-200 ${
            isOpen ? 'text-[#D4896A]' : 'text-[#2C2C2C] group-hover:text-[#D4896A]'
          }`}
          style={{ fontFamily: '\'Playfair Display\', serif' }}
        >
          {faq.question}
        </span>

        {/* Toggle Icon */}
        <div
          className={`w-8 h-8 flex items-center justify-center rounded-full shrink-0 transition-all duration-300 ${
            isOpen ? 'bg-[#D4896A] rotate-45' : 'bg-[#F0E8E0] group-hover:bg-[#EAD8CC]'
          }`}
        >
          <i
            className={`ri-add-line text-lg transition-colors duration-200 ${
              isOpen ? 'text-white' : 'text-[#8A7A6A]'
            }`}
          />
        </div>
      </button>

      {/* Answer */}
      <div
        ref={contentRef}
        className="overflow-hidden transition-all duration-400"
        style={{
          maxHeight: isOpen ? (contentRef.current?.scrollHeight ?? 500) + 'px' : '0px',
          opacity: isOpen ? 1 : 0,
        }}
      >
        <p
          className="pb-6 pl-11 pr-4 text-base text-[#6B6B6B] leading-relaxed"
          style={{ fontFamily: 'Inter, sans-serif' }}
        >
          {faq.answer}
        </p>
      </div>
    </div>
  );
}

export default function FaqSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.08 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="faq" className="bg-white py-24 md:py-32" ref={ref}>
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col lg:flex-row gap-16 xl:gap-24">

          {/* Left — Sticky Header */}
          <div
            className={`lg:w-5/12 lg:sticky lg:top-28 lg:self-start transition-all duration-700 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <span
              className="text-xs font-semibold tracking-widest uppercase text-[#D4896A] block mb-4"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              FAQ
            </span>
            <h2
              className="text-4xl md:text-5xl font-bold text-[#2C2C2C] mb-6 leading-tight"
              style={{ fontFamily: '\'Playfair Display\', serif' }}
            >
              Questions You
              <br />
              Might Be
              <br />
              <span className="text-[#D4896A] italic">Wondering About</span>
            </h2>
            <p
              className="text-base text-[#6B6B6B] leading-relaxed mb-8"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              We know taking a new step can feel uncertain. Here are honest answers
              to the questions we hear most often — because your confidence matters
              before you ever book a session.
            </p>

            {/* Still have questions? */}
            <div className="bg-[#FAF8F5] rounded-2xl p-6">
              <p
                className="text-base font-semibold text-[#2C2C2C] mb-2"
                style={{ fontFamily: '\'Playfair Display\', serif' }}
              >
                Still have questions?
              </p>
              <p
                className="text-sm text-[#6B6B6B] mb-4"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                The best way to get a real answer is a free, no-pressure conversation with us.
              </p>
              <button
                onClick={() => scrollTo('consultation')}
                className="bg-[#D4896A] text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-[#c07a5a] transition-colors duration-300 cursor-pointer whitespace-nowrap"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                Book a Free Consultation
              </button>
            </div>
          </div>

          {/* Right — Accordion */}
          <div
            className={`lg:w-7/12 transition-all duration-700 delay-150 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <div className="divide-y-0">
              {faqs.map((faq, i) => (
                <FaqItem
                  key={faq.question}
                  faq={faq}
                  index={i}
                  isOpen={openIndex === i}
                  onToggle={() => setOpenIndex(openIndex === i ? null : i)}
                  visible={visible}
                />
              ))}
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
